import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

export const notificationSourceV1Schema = z.enum(["field", "dispatch", "customer", "support"])
export const notificationAudienceV1Schema = z.enum([
  "field_worker", "dispatcher", "customer_contact", "support_staff",
])
export const notificationChannelV1Schema = z.enum(["in_app", "email", "sms"])
export const notificationEventV1Schema = z.enum([
  "field_visit_changed",
  "dispatch_publication_changed",
  "customer_action_required",
  "support_case_customer_message",
  "support_case_state_changed",
])

export const notificationPreferenceSetV1Schema = z.object({
  preferenceId: entityIdSchema,
  audience: notificationAudienceV1Schema,
  recipientId: entityIdSchema,
  channel: notificationChannelV1Schema,
  enabled: z.boolean(),
}).strict()

const safeVariables = z.record(
  z.string().regex(/^(reference|status|scheduledAt)$/),
  z.string().trim().min(1).max(128),
).refine((value) => Object.keys(value).length <= 3)

export const notificationCreateV1Schema = z.object({
  notificationId: entityIdSchema,
  dedupeKey: z.string().regex(/^[a-z0-9][a-z0-9:._-]{7,255}$/),
  source: notificationSourceV1Schema,
  sourceEvent: notificationEventV1Schema,
  sourceEntityId: entityIdSchema,
  audience: notificationAudienceV1Schema,
  recipientId: entityIdSchema,
  channel: notificationChannelV1Schema,
  variables: safeVariables,
}).strict().superRefine((value, context) => {
  const eventSource = {
    field_visit_changed: "field",
    dispatch_publication_changed: "dispatch",
    customer_action_required: "customer",
    support_case_customer_message: "support",
    support_case_state_changed: "support",
  } as const
  if (eventSource[value.sourceEvent] !== value.source) {
    context.addIssue({ code: "custom", message: "Notification source mismatch." })
  }
})

export const notificationDeliverV1Schema = z.object({
  notificationId: entityIdSchema,
  providerCode: z.enum(["fake", "in_app", "email", "quo", "twilio"]),
}).strict()

export const notificationProjectionV1Schema = z.object({
  notificationId: entityIdSchema,
  recordVersion: z.number().int().positive(),
  source: notificationSourceV1Schema,
  sourceEvent: notificationEventV1Schema,
  sourceEntityId: entityIdSchema,
  audience: notificationAudienceV1Schema,
  recipientId: entityIdSchema,
  channel: notificationChannelV1Schema,
  providerCode: z.string().nullable(),
  state: z.enum(["pending", "suppressed", "sent", "delivered", "failed", "uncertain"]),
  attemptCount: z.number().int().nonnegative(),
  failureCode: z.string().nullable(),
}).strict()

export type NotificationCreateV1 = z.infer<typeof notificationCreateV1Schema>
export type NotificationProjectionV1 = z.infer<typeof notificationProjectionV1Schema>
